/**
 * Hook que calcula métricas reales desde Supabase trading_journal.
 * Incluye balance, MLL y riesgo calculado desde trades reales.
 */

import { useState, useEffect } from 'react';
import { supabase } from './supabase';

// Balance inicial de la cuenta fondeada (configurable)
const INITIAL_BALANCE = 50_000;
// MLL mínimo absoluto de LucidFlex
const MLL_FLOOR = 47_200;
// Trailing drawdown desde el máximo EOD
const MLL_TRAILING = 2_800;

export interface JournalStats {
  totalTrades: number;
  winners: number;
  losers: number;
  winRate: number;
  totalPnl: number;
  profitFactor: number;
  avgWin: number;
  avgLoss: number;
  period: string;
}

export interface AccountStatus {
  currentBalance: number;
  mllLevel: number;
  distanceToMll: number;
  riskLevel: 'GREEN' | 'YELLOW' | 'RED';
  maxMinis: number;
  maxMicros: number;
  profit: number;
  highestBalance: number;
}

export function useJournalStats(): {
  stats: JournalStats | null;
  account: AccountStatus;
  loading: boolean;
} {
  const [stats, setStats] = useState<JournalStats | null>(null);
  const [account, setAccount] = useState<AccountStatus>({
    currentBalance: INITIAL_BALANCE,
    mllLevel: MLL_FLOOR,
    distanceToMll: INITIAL_BALANCE - MLL_FLOOR,
    riskLevel: 'GREEN',
    maxMinis: 2,
    maxMicros: 20,
    profit: 0,
    highestBalance: INITIAL_BALANCE,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setLoading(false);
          return;
        }

        const { data: trades, error } = await supabase
          .from('trading_journal')
          .select('result, trade_type, trade_date')
          .eq('user_id', user.id)
          .order('trade_date', { ascending: true });

        if (error) throw error;

        // --- Account: balance from trades ---
        let balance = INITIAL_BALANCE;
        let highestEod = INITIAL_BALANCE;
        let lastDate = '';

        // Group P&L by date for EOD trailing MLL
        const dailyPnl: Record<string, number> = {};
        for (const t of (trades || [])) {
          const d = t.trade_date || '';
          dailyPnl[d] = (dailyPnl[d] || 0) + (t.result || 0);
        }

        // Walk through days to compute highest EOD balance
        for (const d of Object.keys(dailyPnl).sort()) {
          balance += dailyPnl[d];
          if (balance > highestEod) {
            highestEod = balance;
          }
          lastDate = d;
        }

        // If no trades, balance stays at initial
        const currentBalance = trades && trades.length > 0
          ? INITIAL_BALANCE + trades.reduce((sum, t) => sum + (t.result || 0), 0)
          : INITIAL_BALANCE;

        // MLL = max(floor, highest_eod - trailing)
        const mll = Math.max(MLL_FLOOR, highestEod - MLL_TRAILING);
        const distance = currentBalance - mll;

        // Risk level
        let riskLevel: 'GREEN' | 'YELLOW' | 'RED';
        if (distance > 1500) riskLevel = 'GREEN';
        else if (distance > 800) riskLevel = 'YELLOW';
        else riskLevel = 'RED';

        // Max contracts by profit tier
        const profit = currentBalance - INITIAL_BALANCE;
        let maxMinis: number;
        if (profit >= 10_000) maxMinis = 12;
        else if (profit >= 5_000) maxMinis = 8;
        else if (profit >= 2_000) maxMinis = 5;
        else if (profit >= 1_000) maxMinis = 3;
        else maxMinis = 2;

        setAccount({
          currentBalance: Math.round(currentBalance * 100) / 100,
          mllLevel: Math.round(mll * 100) / 100,
          distanceToMll: Math.round(distance * 100) / 100,
          riskLevel,
          maxMinis,
          maxMicros: maxMinis * 10,
          profit: Math.round(profit * 100) / 100,
          highestBalance: Math.round(highestEod * 100) / 100,
        });

        // --- Stats ---
        if (!trades || trades.length === 0) {
          setStats(null);
          setLoading(false);
          return;
        }

        const winners = trades.filter(t => (t.result || 0) > 0);
        const losers = trades.filter(t => (t.result || 0) < 0);

        const totalWins = winners.reduce((sum, t) => sum + (t.result || 0), 0);
        const totalLosses = Math.abs(losers.reduce((sum, t) => sum + (t.result || 0), 0));
        const totalPnl = trades.reduce((sum, t) => sum + (t.result || 0), 0);

        const winRate = trades.length > 0
          ? (winners.length / trades.length) * 100
          : 0;

        const profitFactor = totalLosses > 0
          ? totalWins / totalLosses
          : totalWins > 0 ? Infinity : 0;

        const avgWin = winners.length > 0 ? totalWins / winners.length : 0;
        const avgLoss = losers.length > 0 ? totalLosses / losers.length : 0;

        const dates = trades.map(t => t.trade_date).sort();
        const first = dates[0];
        const last = dates[dates.length - 1];
        const period = first === last ? first : `${first} — ${last}`;

        setStats({
          totalTrades: trades.length,
          winners: winners.length,
          losers: losers.length,
          winRate: Math.round(winRate * 10) / 10,
          totalPnl: Math.round(totalPnl * 100) / 100,
          profitFactor: Math.round(profitFactor * 100) / 100,
          avgWin: Math.round(avgWin * 100) / 100,
          avgLoss: Math.round(avgLoss * 100) / 100,
          period,
        });
      } catch (err) {
        console.error('Error fetching journal stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return { stats, account, loading };
}
